import Config from './messages';
import {RESOURCES as locationResources} from './fuseLocationsResourceMap';
import {RESOURCES as productResources} from './fuseProductsResourceMap';
import {RESOURCES as inventoryResources} from './fuseInventoryResourceMap';
import {RESOURCES as loyaltyResources} from './fuseLoyaltyResourceMap';
import {RESOURCES as kitResources} from './fuseKitResourceMap';
import {RESOURCES as searchResources} from './fuseSearchResourceMap';
import {RESOURCES as userResources} from './fuseUserResourceMap';
import {RESOURCES as configResources} from './fuseConfigurationResourceMap';
import {generateUUID} from './shared/functions';
import {
    interceptLoadData,
    createUpsertPayload,
    assignPayload
} from './restClientPreProcess';
import {
    postProcessData,
    postProcessUpsertData,
    setUniqueKeys
} from './restClientPostProcess';

/**
 * Resource Maps for Locations, Products, Inventory, Users, Configuration
 */
const fuseResourceMap = {
    "Locations": locationResources.Locations,
    "LocationNames": locationResources.LocationNames,
    "LocationsForInventory": locationResources.LocationsForInventory,
    "LocationsForTransfer": locationResources.LocationsForTransfer,
    "LocationTSYSTransactionKey": locationResources.LocationTSYSTransactionKey,
    "DBAs": locationResources.DBAs,
    "Companies": locationResources.Companies,
    "LocationTypes": locationResources.LocationTypes,
    "Districts": locationResources.Districts,
    "PriceLevels": locationResources.PriceLevels,
    "AdditionalTaxes": locationResources.AdditionalTaxes,

    "Products": productResources.Products,
    "ProductNames": productResources.ProductNames,
    "ProductUnits": productResources.ProductUnits,
    "ProductUnitCreate": productResources.ProductUnitCreate,
    "ProductTypes": productResources.ProductTypes,
    "ProductCategories": productResources.ProductCategories,
    "ProductCategoryNames": productResources.ProductCategoryNames,
    "ProductAliases": productResources.ProductAliases,
    "Discounts": productResources.Discounts,

    "LocationInventory": inventoryResources.LocationInventory,
    "LocationsWithInventory": searchResources.LocationsWithInventory,
    "LocationsWithInventoryByProductName": searchResources.LocationsWithInventoryByProductName,
    "InventoryRepacksRetailBoxes": inventoryResources.RepacksRetailBoxes,
    "InventoryRepacksRetailBoxesSearch": searchResources.RepacksRetailBoxes,
    "InventoryTransfers": inventoryResources.Transfers,
    "InventoryTransfersByMemo": searchResources.TransfersByMemo,
    "InventoryTransferContents": inventoryResources.TransferContents,
    "InventoryTransferAudits": inventoryResources.TransferAudits,
    "TransferredProducts": searchResources.TransferredProducts,
    "LocationAudits": inventoryResources.LocationAudits,
    "LocationAuditContents": inventoryResources.LocationAuditContents,
    "LocationAuditCollection": inventoryResources.LocationAuditCollection,
    "LocationAuditFinalize": inventoryResources.LocationAuditFinalize,
    "MissingScans": searchResources.MissingScans,

    "LoyaltyRanks": loyaltyResources.LoyaltyRanks,
    "LoyaltyIncentiveTiers": loyaltyResources.LoyaltyIncentiveTiers,
    "VipCardConfigurations": loyaltyResources.VipCardConfigurations,
    "LoyaltyAccounts": loyaltyResources.LoyaltyAccounts,
    "LoyaltyAccountTransactions": loyaltyResources.LoyaltyAccountTransactions,
    "LoyaltyAccountLogs": loyaltyResources.LoyaltyAccountLogs,
    "POSUserTypes": userResources.POSUserTypes,
    "POSKits": kitResources.POSKits,
    "POSUsers": userResources.POSUsers,

    "Configurations": configResources.Configurations,
};

/**
 * Checks for the presence of a valid stored server
 */
const checkServer = () => {
    const server = localStorage.getItem('server');
    if (!server) {
        //console.log('NO SERVER');
    }
};

/**
 * Parse data for Get or Post returns
 */
const parseAck = (type, ack, parseParams, params) => {

    let data = ack.payload;
    if (!data) {
        return;
    }

    // List
    if (type === 'list') {
        let keys = [];

        for (let i = 0; i < data.length; i++) {
            if (parseParams && parseParams.hasNameKeys) {
                // Resources with unique names
                keys.push(data[i].name);
                data[i].id = data[i].name;
            } else if (parseParams && parseParams.needsId) {
                // Resources that need a unique ID
                data[i].id = generateUUID();
                keys.push(data[i].id);
            } else if (parseParams && parseParams.idKey) {
                // Resources with a unique key that is not "id"
                let idValue = null;
                if (parseParams.parentKey) {
                    idValue = data[i][parseParams.parentKey][parseParams.idKey];
                } else {
                    idValue = data[i][parseParams.idKey];
                }
                keys.push(idValue);
                data[i].id = idValue;
            } else {
                keys.push(data[i].id);
            }
        }

        let total = ack.payload.length;

        if (ack.payload_meta && ack.payload_meta.total) {
            total = ack.payload_meta.total;
        }
        if (!total) {
            total = 0;
        }

        return {
            "data": data,
            "ids": keys,
            "total": total
        }
    }

    // Get One: Return either first element in data array or data
    if (type === 'item') {
        if (parseParams && parseParams.needsId) {
            // If the Resource needs a generated ID:
            const id = generateUUID();
            if (data[0]) {
                data[0].id = id;
            } else {
                data.id = id;
            }
        }
        if (parseParams && parseParams.idKey) {
            // If the Resource has a unique key to use as the ID:
            if (data[0]) {
                if (parseParams.parentKey) {
                    data[0].id = data[0][parseParams.parentKey][parseParams.idKey];
                } else {
                    data[0].id = data[0][parseParams.idKey];
                }
            } else {
                if (parseParams.parentKey) {
                    data.id = data[parseParams.parentKey][parseParams.idKey];
                } else {
                    data.id = data[parseParams.idKey];
                }
            }
        }
        return data[0] ? {"data": data[0]} : {"data": data};
    }

    // Create and Edit: Return data
    if (type === 'upsert') {
        return data;
    }

    // Delete: Return data
    if (type === 'delete') {
        return data;
    }

    return {"data": {}}
};

/**
 * Load data from a CloudSent POS Resource Map
 */
export const loadFuseData = (type, resource, params, resolve, reject) => {

    let data = null;
    const emptyData = {data: [{id: '', name: ''}], ids: [''], total: 0};

    // If request lacks data and should not automatically get data, resolve as empty
    if (interceptLoadData(type, resource, params)) {
        resolve(emptyData);
        return;
    }

    let cfg = fuseResourceMap[resource][type](params);
    let rdata;
    let xhr = new XMLHttpRequest();

    xhr.addEventListener("readystatechange", function () {
        if (this.readyState === 4) {
            let ack;
            try {
                ack = JSON.parse(this.responseText);
                // Accommodate calls that deliver a direct payload
                if (this.status === 200 && ack && ack.length > 0) {
                    ack = {
                        payload: ack
                    };
                } else if (ack.success !== true) {
                    if (ack.error_message) {
                        reject('Error: ' + ack.error_message);
                    }
                    reject(Config.fuse.notification.server_error);
                    return;
                }
            } catch (e) {
                reject(Config.fuse.notification.no_data);
                return;
            }

            // Mark resources with no ids or variable unique identifiers
            const parseParams = setUniqueKeys(resource);

            rdata = parseAck(cfg.type, ack, parseParams, params);

            // If no payload is available, display an error
            if (!rdata || (rdata.data && typeof rdata.data !== 'object')) {
                reject('No ' + cfg.title + ' available.');
                return;
            }

            // Post-process data for specific resources
            rdata = postProcessData(resource, type, params, rdata);

            resolve(rdata);
        }
    });

    let method = cfg.method;
    let uri = cfg.uri;

    xhr.open(method, uri);
    xhr.setRequestHeader("Cache-Control", "no-cache");
    const token = localStorage.getItem('token');
    xhr.setRequestHeader("Authorization", "Bearer " + token);
    xhr.send(data);
};

/**
 * Post data through a CloudSent POS Resource Map
 */
const upsertFuseData = (type, resource, params, resolve, reject) => {
    // Retrieve the resource uri
    let cfg = fuseResourceMap[resource][type](params);
    let rdata;
    let xhr = new XMLHttpRequest();
    // Create a payload object
    let payload = createUpsertPayload(resource);
    payload.pkg_uuid = generateUUID();

    xhr.addEventListener("readystatechange", function () {
        if (this.readyState === 4) {
            let ack;
            try {
                ack = JSON.parse(this.responseText);
                if (ack.success !== true) {
                    if (ack.error_message) {
                        reject('Error: ' + ack.error_message);
                    }
                    reject(Config.fuse.notification.server_error);
                    return;
                }
            } catch (e) {
                reject(Config.fuse.notification.no_data);
                return;
            }

            rdata = parseAck(cfg.type, ack, null, params);

            // If no payload is returned, display an error notification
            if (!rdata) {
                reject(Config.fuse.notification.no_data);
            }

            rdata = postProcessUpsertData(resource, type, params, rdata, ack);

            // If payload is a string (message), return the string
            if (typeof rdata !== 'boolean') {
                reject(rdata);
            }

            // If no data key is available:
            if (!params.data && params.previousData) {
                params.data = params.previousData;
            }

            setTimeout(function () {
                resolve({'data': params.data});
            }, 600);
        }
    });

    xhr.open("POST", cfg.uri);
    const token = localStorage.getItem('token');
    xhr.setRequestHeader("Authorization", "Bearer " + token);
    // Assemble the payload model or other object(s)
    payload.payload = assignPayload(type, resource, cfg.data);
    xhr.send(JSON.stringify(payload));
};

export default (type, config = {}, options = {}) => {

    // Check for a valid server before proceeding
    checkServer();

    /**
     * @param {string} type Request type, e.g GET_LIST
     * @param {string} resource Resource name, e.g. "Locations"
     * @param {Object} params Request parameters. Depends on the request type
     * @returns {Promise} the Promise for a REST response
     */
    return (type, resource, params) => {
        if (type === 'GET_LIST' || type === 'GET_ONE' || type === 'GET_MANY' || type === 'GET_MANY_REFERENCE') {
            return new Promise((resolve, reject) => {
                loadFuseData(type, resource, params, resolve, reject);
                return true;
            })
        }
        if (type === 'CREATE' || type === 'UPDATE' || type === 'DELETE') {
            return new Promise((resolve, reject) => {
                upsertFuseData(type, resource, params, resolve, reject);
                return true;
            })
        }
    }
}



// WEBPACK FOOTER //
// ./src/Fuse/restClient.js